<script setup lang="ts">
import type { FrontendUserItem, FrontendUserPortraitPayload } from '@/api/modules/frontendUser'
import AppDetailSection from '@/components/AppDisplay/DetailSection.vue'
import AppSectionHeading from '@/components/AppDisplay/SectionHeading.vue'
import AppStatsCards from '@/components/AppDisplay/StatsCards.vue'
import { formatDateTime, profileCompleteness } from '../../utils/userDisplay'

defineOptions({ name: 'UserOverviewTab' })

const props = defineProps<{
  user: FrontendUserItem
  portrait: FrontendUserPortraitPayload | null
}>()

const stats = computed(() => [
  { label: '资料完整度', value: `${profileCompleteness(props.user)}%`, hint: '按公开资料字段估算', tone: 'primary' as const },
  { label: '内容资产', value: props.portrait?.content.post_count ?? '-', hint: `商品 ${props.portrait?.content.goods_count ?? '-'} 件` },
  { label: '交易规模', value: (props.portrait?.trade.buy_order_count ?? 0) + (props.portrait?.trade.sell_order_count ?? 0), hint: `退款 ${props.portrait?.trade.refund_count ?? 0} 笔`, tone: 'info' as const },
  { label: '风险事件', value: props.portrait?.risk.risk_event_count ?? 0, hint: '举报、投诉和聊天风控合计', tone: (props.portrait?.risk.risk_event_count ?? 0) > 0 ? 'warning' as const : 'success' as const },
])

const identityItems = computed(() => [
  { label: '用户 ID', value: props.user.id, copyable: true },
  { label: 'UUID', value: props.user.uuid || '-', copyable: Boolean(props.user.uuid) },
  { label: '账号', value: props.user.username || '-', copyable: Boolean(props.user.username) },
  { label: '昵称', value: props.user.nickname || '-' },
  { label: '展示名', value: props.user.display_name || '-' },
  { label: '真实姓名', value: props.user.realname || '-' },
  { label: '手机号', value: props.user.phone || '-', copyable: Boolean(props.user.phone) },
  { label: '邮箱', value: props.user.email || '-', copyable: Boolean(props.user.email) },
  { label: '生日', value: props.user.birthday || '-' },
  { label: '常驻地区', value: [props.user.province, props.user.city, props.user.district].filter(Boolean).join(' / ') || '-' },
  { label: '详细地址', value: props.user.address || '-', span: 2 },
])

const activityItems = computed(() => [
  { label: '注册时间', value: formatDateTime(props.user.created_at) },
  { label: '最近登录', value: formatDateTime(props.user.last_login_time, '暂无登录记录') },
  { label: '累计登录', value: `${props.user.login_count || 0} 次` },
  { label: '注册来源', value: props.user.register_source || '未记录' },
  { label: '注册 IP', value: props.user.register_ip || '未记录', copyable: Boolean(props.user.register_ip) },
  { label: '最近登录 IP', value: props.user.last_login_ip || '未记录', copyable: Boolean(props.user.last_login_ip) },
  { label: '最近登录方式', value: props.user.last_login_type || '未记录' },
  { label: '更新时间', value: formatDateTime(props.user.updated_at) },
  { label: '等级/积分', value: `Lv.${props.user.level || 0} / ${props.user.points || 0}` },
])

const businessItems = computed(() => [
  { label: '邀请人 ID', value: props.user.pid || '无' },
  { label: '直推人数', value: props.user.total_direct || 0 },
  { label: '有效直推', value: props.user.total_valid_direct || 0 },
  { label: '买家成交额', value: `¥${Number(props.portrait?.trade.buy_paid_amount || 0).toFixed(2)}` },
  { label: '卖家成交额', value: `¥${Number(props.portrait?.trade.sell_paid_amount || 0).toFixed(2)}` },
  { label: '职业', value: props.user.occupation || '-' },
  { label: '星座', value: props.user.constellation || '-' },
  { label: '身高', value: props.user.height ? `${props.user.height} cm` : '-' },
])

const profileItems = computed(() => [
  { label: '个性签名', value: props.user.signature || '-', span: 2 },
  { label: '一句话简介', value: props.user.intro || '-', span: 2 },
  { label: '详细介绍', value: props.user.introduce || '-', span: 2 },
])
</script>

<template>
  <div class="space-y-4">
    <AppStatsCards :items="stats" />
    <div class="gap-4 grid xl:grid-cols-2">
      <AppDetailSection title="身份与联系方式" description="账户身份、实名与联系信息" icon="i-ri:user-settings-line" :items="identityItems" :columns="2" />
      <AppDetailSection title="账号与活跃" description="注册来源、登录轨迹与成长数据" icon="i-ri:pulse-line" :items="activityItems" :columns="2" />
    </div>
    <div class="gap-4 grid xl:grid-cols-2">
      <AppDetailSection title="业务资料" description="邀请关系、交易规模与基础画像" icon="i-ri:bar-chart-box-line" :items="businessItems" :columns="2" />
      <AppDetailSection title="公开资料" description="面向前台展示的个人资料" icon="i-ri:profile-line" :items="profileItems" :columns="2" />
    </div>
    <section class="detail-section">
      <AppSectionHeading title="当前判断" description="账号、注销与近期风险结论" icon="i-ri:shield-check-line" />
      <div class="gap-3 grid md:grid-cols-3">
        <div class="border-default bg-subtle p-4 border rounded-3">
          <div class="text-xs text-muted-foreground">
            账号状态
          </div>
          <div class="text-sm font-medium mt-2">
            {{ Number(user.status) === 1 ? '正常使用' : '已禁用' }}
          </div>
        </div>
        <div class="border-default bg-subtle p-4 border rounded-3">
          <div class="text-xs text-muted-foreground">
            注销流程
          </div>
          <div class="text-sm font-medium mt-2">
            {{ user.account_deletion_summary?.status || '无进行中申请' }}
          </div>
        </div>
        <div class="border-default bg-subtle p-4 border rounded-3">
          <div class="text-xs text-muted-foreground">
            最近风险
          </div>
          <div class="text-sm font-medium mt-2">
            {{ user.message_risk_summary?.latest_reject_reason || '暂无风险命中' }}
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
